import { inferTenantFromHost, type TenantConfig } from '#imports'

export default function useTenant() {
  // Use cookie name from nuxt config, or fallback to 'tenant'
  // This is mainly to avoid clashes in development on localhost.
  const tenantCookieName = useRuntimeConfig().public.tenants.cookie || 'tenant'

  const { state: tenant, initialize: initializeTenant } = useSecureState<string>(tenantCookieName)

  const initialized = useState('tenant-initialized', () => false)

  const { isApp } = usePlatform()

  async function setInitialTenant() {
    // I have no idea why, but as soon as we run this in non-app layer, we get a nuxt error
    // A guess this fix is fine because we don't need to do something async in non-app layer anyway.
    if (isApp) {
      await initializeTenant()
    }

    const requestUrl = useRequestURL()
    const host = requestUrl.host
    const publicConfig = useRuntimeConfig().public
    const tenantsConfig = publicConfig.tenants as TenantConfig
    const defaultTenant = tenantsConfig.default

    // Check if we're in development mode and a tenant is set via query param
    // If this works well, we can delete the middleware.
    if (import.meta.env.DEV) {
      const tenantParam = requestUrl.searchParams.get('tenant')
      if (tenantParam) {
        // console.log('[useTenant] Using tenant from query param:', tenantParam)
        tenant.value = tenantParam
        return
      }
    }

    // Determine tenant from host using shared util
    const inferred = inferTenantFromHost(host, tenantsConfig)
    if (inferred) {
      tenant.value = inferred
      return
    }

    // Fall back to default tenant if no match found, and tenant cookie is not set
    if (!tenant.value) {
      tenant.value = defaultTenant
    }

    if (!tenant.value) {
      console.error('[useTenant] No tenant found')
      throw new Error('[useTenant] No tenant found')
    }

    initialized.value = true
  }

  // This needs to be async in capacitor layer, so we moved it to a middleware
  if (!isApp) {
    setInitialTenant()
  }

  return { tenant, initialize: setInitialTenant }
}
